import { manufacturingFetch } from "@/lib/manufacturingClient";
import type {
  MfgInventoryCountLinesResponse,
  MfgInventoryCountLineUpdateResponse,
  MfgInventoryCountOpenSessionResponse,
  MfgInventoryCountProposalCreateResponse,
  MfgInventoryCountSessionArchiveResponse,
  MfgInventoryCountProposalReviewResponse,
  MfgInventoryCountProposalsListResponse,
  MfgInventoryCountProposalStatus,
  MfgInventoryCountSessionCloseResponse,
  MfgInventoryCountSessionOpenResponse,
} from "@/lib/manufacturing/types/inventoryCount";

export function openManufacturingInventoryCountSession(payload?: {
  plant_id?: string;
  notes?: string;
}) {
  return manufacturingFetch<MfgInventoryCountSessionOpenResponse>(
    "/api/mfg/inventory-count/sessions/open",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload ?? {}),
    }
  );
}

export function closeManufacturingInventoryCountSession(session_id: string) {
  return manufacturingFetch<MfgInventoryCountSessionCloseResponse>(
    "/api/mfg/inventory-count/sessions/close",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id }),
    }
  );
}

/** Discard an open count without posting stock adjustments (status = archived). */
export function archiveManufacturingInventoryCountSession(session_id: string) {
  return manufacturingFetch<MfgInventoryCountSessionArchiveResponse>(
    "/api/mfg/inventory-count/sessions/archive",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id }),
    }
  );
}

export function listManufacturingInventoryCountLines(opts: {
  session_id: string;
  plant_id?: string;
}) {
  return manufacturingFetch<MfgInventoryCountLinesResponse>("/api/mfg/inventory-count/lines", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts),
  });
}

export function getOpenManufacturingInventoryCountSession(opts?: { plant_id?: string }) {
  return manufacturingFetch<MfgInventoryCountOpenSessionResponse>(
    "/api/mfg/inventory-count/lines",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ open_only: true, ...(opts ?? {}) }),
    }
  );
}

/** MIC-2 — set counted_qty (or null to clear). */
export function updateManufacturingInventoryCountLine(opts: {
  line_id: string;
  counted_qty: number | null;
}) {
  return manufacturingFetch<MfgInventoryCountLineUpdateResponse>(
    "/api/mfg/inventory-count/lines/update",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(opts),
    }
  );
}

/** MIC-3 — propose a new material from an open count session. */
export function createManufacturingInventoryCountProposal(opts: {
  session_id: string;
  proposed_name: string;
  proposed_unit?: string;
  proposed_category?: string;
  proposed_qty?: number | null;
  notes?: string;
}) {
  return manufacturingFetch<MfgInventoryCountProposalCreateResponse>(
    "/api/mfg/inventory-count/proposals/create",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(opts),
    }
  );
}

export function listManufacturingInventoryCountProposals(opts?: {
  session_id?: string;
  status?: MfgInventoryCountProposalStatus;
  limit?: number;
}) {
  return manufacturingFetch<MfgInventoryCountProposalsListResponse>(
    "/api/mfg/inventory-count/proposals",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(opts ?? {}),
    }
  );
}

export function reviewManufacturingInventoryCountProposal(opts: {
  proposal_id: string;
  action: "approve" | "reject";
}) {
  return manufacturingFetch<MfgInventoryCountProposalReviewResponse>(
    "/api/mfg/inventory-count/proposals/review",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(opts),
    }
  );
}
